import { useCallback, useEffect, useState } from "react";
import { Select, Upload as AntUpload, Image } from "antd";
import {
  Boxes, Plus, Eye, Package, Clock, CheckCircle2, XCircle, RotateCcw, Ban, X,
  UploadCloud, Paperclip, Pencil,
} from "lucide-react";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { ASSET_REQUEST, MASTER_ASSET_CATEGORY } from "../../constants/api";
import {
  ASSET_REQUEST_TYPES, ASSET_REQUEST_PRIORITIES,
  DEFAULT_ASSET_REQUEST_PRIORITY, DEFAULT_ASSET_REQUEST_TYPE, assetReqPriorityClass,
} from "../../constants/assetRequest";
import {
  StatusBadge, PriorityBadge, TypeBadge, Timeline, DetailRows, fmtDate, fmtDateTime,
} from "../assetmanagement/AssetRequestPieces";

const MAX_FILE_MB = 5;

/* Employee Asset Request — same shape as Leave / Reimbursement / Advance:
   a Reactable list of the employee's own tickets + a creation modal + a detail
   (track) modal. No global drawer; a page-local FAB raises a new request. */

const STATUS_BANNER = {
  Pending: { t: "Awaiting Review", d: "Your request is in the admin queue.", Icon: Clock },
  Approved: { t: "Approved", d: "An asset is being allocated to you.", Icon: CheckCircle2 },
  Issued: { t: "Asset Issued", d: "Your asset has been allocated.", Icon: CheckCircle2 },
  Rejected: { t: "Request Closed", d: "This request was not approved.", Icon: XCircle },
  Cancelled: { t: "Request Withdrawn", d: "You withdrew this request.", Icon: Ban },
  Returned: { t: "Asset Returned", d: "Returned to inventory.", Icon: RotateCcw },
};

const ITHelpDesk = () => {
  const { fetch, create, update } = useApiMethods();
  const companyId = sessionStorage.getItem("userId");

  const [requests, setRequests] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [serverTrigger, setServerTrigger] = useState(false);
  const [categories, setCategories] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editReq, setEditReq] = useState(null); // request being edited (Pending only)
  const [detail, setDetail] = useState(null);

  const refresh = () => setServerTrigger((t) => !t);

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch(MASTER_ASSET_CATEGORY, { drop_down: "True" });
      if (res?.status === "success") setCategories(res.data || []);
    } catch (e) {
      ToastMsg("error", e.message);
    }
    // `fetch` is a new ref every render — keep it out of deps to avoid a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const columns = [
    { accessor: "request_no", Header: "Request", filterable: true, sortable: true,
      Cell: ({ value }) => <span className="font-bold text-sidebar-primary">{value ?? "-"}</span> },
    { accessor: "subject", Header: "Subject", filterable: true,
      Cell: ({ value }) => <span className="font-medium">{value || "-"}</span> },
    { accessor: "category_name", Header: "Category", Cell: ({ value }) => value ?? "-" },
    { accessor: "request_type", Header: "Issue Type",
      Cell: ({ value }) => <TypeBadge type={value} /> },
    { accessor: "priority", Header: "Priority",
      Cell: ({ value }) => <PriorityBadge priority={value} /> },
    { accessor: "status", Header: "Status", filterable: true,
      Cell: ({ value }) => <StatusBadge status={value} /> },
    { accessor: "created_at", Header: "Submitted", sortable: true,
      Cell: ({ value }) => <span className="text-xs text-muted-foreground">{fmtDate(value)}</span> },
    { accessor: "Action", Header: "",
      Cell: ({ row }) => (
        <Button variant="ghost" size="sm" className="text-sidebar-primary"
          onClick={(e) => { e.stopPropagation(); setDetail(row); }}>
          <Eye className="h-4 w-4" />
        </Button>
      ) },
  ];

  return (
    <ContainerCard>
      <ModuleTitleCard
        Icon={Boxes}
        title="IT Help Desk"
        subtitle="Raise an asset request and track its status"
        totalRecords={totalRecords}
        buttonEnable
        buttonName="New Request"
        setOpenDialog={() => setCreateOpen(true)}
      />

      <Reactable
        columns={columns}
        data={requests}
        setData={setRequests}
        setTotalCount={setTotalRecords}
        url={ASSET_REQUEST}
        filter={{ mine: "True" }}
        serverSide
        serverTrigger={serverTrigger}
        isCSVRequired
        isPDFRequired
        tableHeight="calc(100vh - 330px)"
        rowSize="sm"
        onRowClick={(row) => setDetail(row)}
      />

      {(createOpen || editReq) && (
        <CreateModal
          categories={categories}
          companyId={companyId}
          create={create}
          update={update}
          editReq={editReq}
          onClose={() => { setCreateOpen(false); setEditReq(null); }}
          onSaved={() => { setCreateOpen(false); setEditReq(null); refresh(); }}
        />
      )}

      {detail && (
        <DetailModal
          req={detail}
          update={update}
          onClose={() => setDetail(null)}
          onChanged={() => { setDetail(null); refresh(); }}
          onEdit={() => { const r = detail; setDetail(null); setEditReq(r); }}
        />
      )}
    </ContainerCard>
  );
};

/* ------------------------------ create modal ------------------------------ */

const CreateModal = ({ categories, companyId, create, update, editReq, onClose, onSaved }) => {
  const isEdit = !!editReq;
  const [form, setForm] = useState({
    category: editReq?.category ?? null,
    request_type: editReq?.request_type ?? DEFAULT_ASSET_REQUEST_TYPE,
    priority: editReq?.priority ?? DEFAULT_ASSET_REQUEST_PRIORITY,
    subject: editReq?.subject ?? "",
    justification: editReq?.justification ?? "",
  });
  const [files, setFiles] = useState([]); // File[] (new attachments only)
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reject oversized files; returning false stops antd's auto-upload.
  const onPick = (file) => {
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      ToastMsg("error", `${file.name} exceeds ${MAX_FILE_MB}MB.`);
      return false;
    }
    setFiles((prev) => [...prev, file]);
    return false;
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.category) { setErr("Please choose an asset category."); return; }
    if (!form.subject.trim()) { setErr("Please add a short subject."); return; }
    if (!form.justification.trim()) { setErr("Please add a description / justification."); return; }
    setErr("");
    setLoading(true);
    try {
      let res;
      if (isEdit) {
        // Edit (Pending only) — JSON update of the editable fields.
        res = await update(ASSET_REQUEST, {
          id: editReq.id,
          category: form.category,
          request_type: form.request_type,
          priority: form.priority,
          subject: form.subject.trim(),
          justification: form.justification.trim(),
        });
      } else {
        // Multipart so screenshots / error photos ride along with the ticket.
        const fd = new FormData();
        if (companyId) fd.append("company_id", String(parseInt(companyId)));
        fd.append("category", form.category);
        fd.append("request_type", form.request_type);
        fd.append("priority", form.priority);
        fd.append("subject", form.subject.trim());
        fd.append("justification", form.justification.trim());
        files.forEach((file, i) => fd.append(`attachment_${i + 1}`, file));
        res = await create(ASSET_REQUEST, fd, true);
      }
      if (res?.status === "success") {
        ToastMsg("success", res.message || (isEdit ? "Request updated." : "Request submitted."));
        onSaved();
      } else {
        ToastMsg("error", res?.message || "Could not save request.");
      }
    } catch (e) {
      ToastMsg("error", e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogModal
      open
      onClose={onClose}
      onSave={submit}
      title={isEdit ? "Edit Asset Request" : "New Asset Request"}
      saveLabel={isEdit ? "Save Changes" : "Submit Request"}
      loading={loading}
      width="520px"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Asset Category" required>
            <Select
              size="large"
              value={form.category}
              onChange={(v) => set("category", v)}
              className="w-full"
              placeholder="Select a category"
              showSearch
              optionFilterProp="label"
              options={categories.map((c) => ({ label: c.category_name, value: c.id }))}
              notFoundContent="No categories configured"
            />
          </Field>

          <Field label="Issue Type" required>
            <Select
              size="large"
              value={form.request_type}
              onChange={(v) => set("request_type", v)}
              className="w-full"
              options={ASSET_REQUEST_TYPES.map((t) => ({ label: t, value: t }))}
            />
          </Field>
        </div>

        <Field label="Priority" required>
          <div className="grid grid-cols-4 gap-2">
            {ASSET_REQUEST_PRIORITIES.map((p) => {
              const sel = form.priority === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => set("priority", p)}
                  className={cn(
                    "rounded-lg border px-3 py-2 text-sm font-semibold transition-colors",
                    sel
                      ? cn("border-transparent ring-2 ring-offset-1 ring-sidebar-primary/40", assetReqPriorityClass(p))
                      : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300"
                  )}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Subject" required>
          <Input
            value={form.subject}
            onChange={(e) => set("subject", e.target.value)}
            placeholder="e.g. Laptop not powering on"
            maxLength={120}
          />
        </Field>

        <Field label="Description / Justification" required>
          <Textarea
            rows={4}
            value={form.justification}
            onChange={(e) => set("justification", e.target.value)}
            placeholder="Detailed explanation — what happened, since when, anything you've tried…"
          />
        </Field>

        {!isEdit && (
        <Field label="Attachments">
          <AntUpload.Dragger
            accept="image/*,.pdf"
            multiple
            beforeUpload={onPick}
            showUploadList={false}
            className="dark:bg-gray-800 dark:border-gray-700"
          >
            <UploadCloud className="mx-auto mb-2 h-8 w-8 text-gray-400" />
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Click or drag to upload</p>
            <p className="text-xs text-gray-500">
              Screenshots, error messages, photos of damaged devices · .jpg .png .pdf (max {MAX_FILE_MB}MB)
            </p>
          </AntUpload.Dragger>

          {files.length > 0 && (
            <div className="mt-2 space-y-2">
              {files.map((file, i) => (
                <div key={i} className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2">
                  {file.type?.startsWith("image/") ? (
                    <Image src={URL.createObjectURL(file)} alt={file.name} width={40} height={40} className="rounded object-cover" />
                  ) : (
                    <span className="grid h-10 w-10 place-items-center rounded bg-gray-100 dark:bg-gray-700 text-gray-500">
                      <Paperclip className="h-4 w-4" />
                    </span>
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-200">{file.name}</span>
                  <Button size="sm" variant="outline" onClick={() => removeFile(i)}>Remove</Button>
                </div>
              ))}
            </div>
          )}
        </Field>
        )}

        {err && <p className="text-xs font-medium text-red-500">{err}</p>}
      </div>
    </DialogModal>
  );
};

/* ------------------------------ detail modal ------------------------------ */

const DetailModal = ({ req, update, onClose, onChanged, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const banner = STATUS_BANNER[req.status] || STATUS_BANNER.Pending;
  const BIcon = banner.Icon;
  const hasAsset = req.assigned_asset_name || req.assigned_asset_tag;

  const withdraw = async () => {
    setLoading(true);
    try {
      const res = await update(ASSET_REQUEST, {
        id: req.id, status: "Cancelled", decision_note: "Withdrawn by employee",
      });
      if (res?.status === "success") {
        ToastMsg("success", "Request withdrawn.");
        onChanged();
      } else {
        ToastMsg("error", res?.message || "Could not withdraw.");
      }
    } catch (e) {
      ToastMsg("error", e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogModal
      open
      onClose={onClose}
      title={req.request_no || "Asset Request"}
      showActions={false}
      width="520px"
    >
      <div className="space-y-4">
        {/* heading */}
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-sidebar-primary/10 text-sidebar-primary">
            <Package className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold">{req.subject || `${req.category_name || "Asset"} Request`}</h2>
            <p className="text-xs text-muted-foreground">{req.category_name || "—"}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              <TypeBadge type={req.request_type} full />
              <PriorityBadge priority={req.priority} />
              <StatusBadge status={req.status} />
            </div>
          </div>
        </div>

        {/* status banner */}
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-3.5">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-sidebar-primary/10 text-sidebar-primary">
            <BIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{banner.t}</p>
            <p className="text-xs text-muted-foreground">{req.decision_note || banner.d}</p>
          </div>
        </div>

        {/* description */}
        <div>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Description</p>
          <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">{req.justification || "—"}</p>
        </div>

        {/* attachments */}
        {Array.isArray(req.attachments) && req.attachments.length > 0 && (
          <div>
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Attachments</p>
            <div className="flex flex-col gap-1.5">
              {req.attachments.map((a, i) => {
                const url = typeof a === "string" ? a : a?.url;
                const name = (typeof a === "object" && a?.name) || `Attachment ${i + 1}`;
                return (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2 text-sm text-sidebar-primary hover:underline">
                    <Paperclip className="h-4 w-4 shrink-0" />
                    <span className="truncate">{name}</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* issued asset */}
        {hasAsset && (
          <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3.5 dark:border-green-900/40 dark:bg-green-900/20">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300">
              <Package className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{req.assigned_asset_name || "Assigned asset"}</p>
              <p className="text-xs text-muted-foreground">{req.assigned_asset_tag}</p>
            </div>
          </div>
        )}

        {/* details */}
        <div>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Details</p>
          <DetailRows
            rows={[
              ["Submitted", fmtDateTime(req.created_at)],
              ["Approved", fmtDateTime(req.approved_at)],
              ["Issued", fmtDateTime(req.issued_at)],
              ["Asset", req.assigned_asset_tag || req.assigned_asset_name || "—"],
            ]}
          />
        </div>

        {/* timeline */}
        <div>
          <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Timeline</p>
          <Timeline log={req.approval_log || []} />
        </div>

        {/* footer */}
        <div className="flex justify-end gap-2.5 border-t border-gray-100 dark:border-gray-800 pt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {req.status === "Pending" && (
            <>
              <Button variant="outline" onClick={onEdit} disabled={loading}>
                <Pencil className="h-4 w-4" /> Edit
              </Button>
              <Button variant="destructive" onClick={withdraw} disabled={loading}>
                <X className="h-4 w-4" /> {loading ? "Withdrawing…" : "Withdraw"}
              </Button>
            </>
          )}
        </div>
      </div>
    </DialogModal>
  );
};

/* -------------------------------- helper --------------------------------- */

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    {children}
  </div>
);

export default ITHelpDesk;
